import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthFetch } from "@/hooks/useAuthFetch";
import { useAuth } from "@/contexts/AuthContext";
import { VoiceMode } from "@/components/voice/VoiceMode";
import type {
  Interview,
  InterviewResponse,
  VoiceMessage,
  TranscriptEntry,
  InterviewQuestion,
  Attachment,
} from "@acde/shared";

const API_URL = import.meta.env.VITE_API_URL || "/api";

/** Option label for multiple choice: A–Z, then AA, AB, … so any number of options is supported. */
function getOptionLabel(index: number): string {
  if (index < 26) return String.fromCharCode(65 + index);
  const first = Math.floor(index / 26) - 1;
  const second = index % 26;
  return String.fromCharCode(65 + first) + String.fromCharCode(65 + second);
}

// Retry configuration for Cosmos DB eventual consistency
// Increased from 3x1000ms to 5x2000ms for more reliable access after publishing
const PROPAGATION_MAX_RETRIES = 5;
const PROPAGATION_DELAY_MS = 2000;

// Loading states for proper initialization sequence
type InitState = "waiting_auth" | "loading_interview" | "ready" | "error";

type SpeechRecognitionResultLike = { transcript: string };
type SpeechRecognitionResultItemLike = {
  isFinal: boolean;
  0: SpeechRecognitionResultLike;
};
type SpeechRecognitionResultListLike = ArrayLike<SpeechRecognitionResultItemLike>;
type SpeechRecognitionEventLike = Event & {
  resultIndex: number;
  results: SpeechRecognitionResultListLike;
};
type SpeechRecognitionErrorEventLike = Event & { error?: string };
type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  start: () => void;
  stop: () => void;
};
type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

export function RespondPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { authFetch } = useAuthFetch();
  const { isReady, user } = useAuth();

  // Initialization state - prevents race conditions
  const [initState, setInitState] = useState<InitState>("waiting_auth");
  const initAttemptedRef = useRef(false);

  // Interview and response state
  const [interview, setInterview] = useState<Partial<Interview> | null>(null);
  const [response, setResponse] = useState<InterviewResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Conversation state
  const [messages, setMessages] = useState<VoiceMessage[]>([]);
  const [textInput, setTextInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [lastSavedMessageCount, setLastSavedMessageCount] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Voice/Speech state
  const [isVoiceOpen, setIsVoiceOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  // File upload state
  const [attachments, setAttachments] = useState<
    Array<{
      id: string;
      name: string;
      url: string;
      type: string;
      size: number;
      uploadedAt: string;
      previewUrl?: string;
      analysis?: string;
    }>
  >([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Get current question - memoized to prevent useCallback dependency issues
  const questions = useMemo(
    () => (interview?.questions || []) as InterviewQuestion[],
    [interview?.questions]
  );
  const currentQuestion = questions[currentQuestionIndex];

  const formatQuestionPrompt = useCallback((question: InterviewQuestion) => {
    const baseText = `**${question.text}**`;

    if (question.type === "multiple_choice") {
      const options = (question.options || [])
        .map((option) => option.trim())
        .filter(Boolean);
      if (options.length === 0) return baseText;
      const optionLines = options
        .map((option, i) => `${getOptionLabel(i)}. ${option}`)
        .join("\n");
      return `${baseText}\n\nOptions:\n${optionLines}\n\nYou can choose one of these or share another answer.`;
    }

    if (question.type === "rating") {
      return `${baseText}\n\nPlease rate on a scale of 1 to 5.`;
    }

    if (question.type === "yes_no") {
      return `${baseText}\n\nPlease answer Yes or No.`;
    }

    return baseText;
  }, []);

  const currentQuestionOptions = useMemo(() => {
    if (!currentQuestion) return [];

    if (currentQuestion.type === "multiple_choice") {
      return (currentQuestion.options || [])
        .map((option) => option.trim())
        .filter(Boolean);
    }

    if (currentQuestion.type === "rating") {
      return ["1", "2", "3", "4", "5"];
    }

    if (currentQuestion.type === "yes_no") {
      return ["Yes", "No"];
    }

    return [];
  }, [currentQuestion]);

  const showChoiceOptions = currentQuestionOptions.length > 0;
  const inputPlaceholder = showChoiceOptions
    ? "Select an option or type your response..."
    : "Type your response...";

  // Generate interviewer greeting
  const getInterviewerGreeting = useCallback(() => {
    const firstName = user?.name?.split(" ")[0] || "there";
    const title = interview?.title || "the interview";

    if (questions.length > 0) {
      return `Hi ${firstName}! Thanks for participating in "${title}". I'll be guiding you through ${questions.length} questions today. Let's start with the first one:\n\n${formatQuestionPrompt(
        questions[0]
      )}`;
    }
    return `Hi ${firstName}! Thanks for joining "${title}". I'm here to learn about your experiences and insights. Feel free to share your thoughts openly - there are no wrong answers. What would you like to start with?`;
  }, [user, interview, questions, formatQuestionPrompt]);

  // Track how many follow-ups we've asked for the current question
  const followUpCountRef = useRef(0);
  const lastQuestionIndexRef = useRef(0);

  // Generate AI response based on conversation context
  const generateAIResponse = useCallback(
    (userMessage: string): string => {
      // Neutral acknowledgments that work for ANY question type
      const acknowledgments = [
        "Thanks for sharing that!",
        "Got it, that's helpful.",
        "Thanks, that gives good context.",
        "Appreciate you sharing that.",
        "That's great to know, thanks!",
      ];
      const pick = (arr: string[]) =>
        arr[Math.floor(Math.random() * arr.length)];

      const trimmedMessage = userMessage.trim();
      const normalizedMessage = trimmedMessage.toLowerCase();
      const isStructuredQuestion =
        currentQuestion?.type === "multiple_choice" ||
        currentQuestion?.type === "rating" ||
        currentQuestion?.type === "yes_no";
      const hasChoiceOptions =
        isStructuredQuestion && currentQuestionOptions.length > 0;
      const matchedChoice = hasChoiceOptions
        ? currentQuestionOptions.find(
            (option) => option.toLowerCase() === normalizedMessage
          )
        : undefined;

      // Reset follow-up counter when question changes
      if (currentQuestionIndex !== lastQuestionIndexRef.current) {
        followUpCountRef.current = 0;
        lastQuestionIndexRef.current = currentQuestionIndex;
      }

      const isLastQuestion = currentQuestionIndex >= questions.length - 1;

      // Helper: advance to next question with an acknowledgment prefix
      const advanceToNext = (prefix: string) => {
        const nextIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(nextIndex);
        setAnsweredQuestions(nextIndex);
        followUpCountRef.current = 0;
        lastQuestionIndexRef.current = nextIndex;
        const nextQ = questions[nextIndex];
        return `${prefix} Here's the next question:\n\n${formatQuestionPrompt(nextQ)}`;
      };

      // ----- 1. Move-on detection (broad natural language) -----
      const moveOnPhrases = [
        "next", "done", "move on", "next question", "go to the next",
        "that's all", "thats all", "that is all", "continue",
        "nothing else", "nothing more", "not much more",
        "no more to tell", "not more to tell",
        "nothing to add", "not much to add",
        "nothing more to tell", "nothing more to say", "nothing more to share",
        "already said", "already told", "already mentioned", "already explained",
        "finished", "finish", "skip", "pass",
        "i'm done", "im done", "lets move on", "let's move on",
        "please move on", "can we move on",
        "i don't have more", "i dont have more",
        "that's about it", "thats about it", "that covers it",
        "i think that's it", "i think thats it",
        "not really much more", "don't have much more", "dont have much more",
        "can't think of anything", "cant think of anything",
        "i've said enough", "ive said enough",
        "no", "nope", "nah", "not really",
      ];
      const wantsToMoveOn = moveOnPhrases.some((phrase) =>
        normalizedMessage.includes(phrase)
      );

      // Detect disengagement: repeated short answers on the same question
      const recentUserMessages = messages
        .filter((m) => m.role === "user")
        .slice(-3)
        .map((m) => m.content.toLowerCase());
      const isDisengaged =
        recentUserMessages.length >= 2 &&
        recentUserMessages.every((m) => m.length < 40) &&
        trimmedMessage.length < 40 &&
        followUpCountRef.current >= 1;

      const shouldMoveOn = wantsToMoveOn || isDisengaged;

      if (shouldMoveOn) {
        if (!isLastQuestion) {
          return advanceToNext("Got it, let's move on.");
        }
        setAnsweredQuestions(questions.length);
        return "You've covered all the questions! Feel free to add any final thoughts, or click **Submit** when you're ready to finish.";
      }

      // ----- 2. Structured question: matched a specific option -----
      if (matchedChoice) {
        if (!isLastQuestion) {
          return advanceToNext(`Thanks for selecting "${matchedChoice}".`);
        }
        return `Thanks for selecting "${matchedChoice}". You can add any context or click **Submit** when you're ready.`;
      }

      followUpCountRef.current++;

      // ----- 3. Short / direct answer (< 50 chars) -----
      if (trimmedMessage.length < 50) {
        if (hasChoiceOptions) {
          if (!isLastQuestion) {
            return `${pick(acknowledgments)} Would you like to add anything, or shall we move to the next question?`;
          }
          return `${pick(acknowledgments)} Anything else you'd like to add?`;
        }

        // First short answer: ask once if they want to elaborate
        if (followUpCountRef.current === 1) {
          if (!isLastQuestion) {
            return `${pick(acknowledgments)} Would you like to add anything else, or shall we move to the next question?`;
          }
          return `${pick(acknowledgments)} Would you like to elaborate, or are you happy to move on?`;
        }

        // Second short answer on same question: auto-advance
        if (!isLastQuestion) {
          return advanceToNext(pick(acknowledgments));
        }
        setAnsweredQuestions(questions.length);
        return `${pick(acknowledgments)} You've covered all the questions — click **Submit** when you're ready!`;
      }

      // ----- 4. Substantial answer (>= 50 chars) -----

      // First substantial answer: use the question's followUpPrompt if available
      if (followUpCountRef.current === 1 && currentQuestion?.followUpPrompt) {
        return currentQuestion.followUpPrompt;
      }

      // Otherwise: acknowledge and offer to continue or move on
      if (!isLastQuestion) {
        return `${pick(acknowledgments)} Is there anything else you'd like to add, or shall we move to the next question?`;
      }
      return `${pick(acknowledgments)} Would you like to add anything else, or are you ready to submit?`;
    },
    [
      currentQuestion,
      currentQuestionIndex,
      currentQuestionOptions,
      formatQuestionPrompt,
      messages,
      questions,
    ]
  );

  // Start or load response - with race condition prevention and retry logic for eventual consistency
  useEffect(() => {
    // Guard: wait for auth to be ready
    if (!isReady) {
      setInitState("waiting_auth");
      return;
    }

    // Guard: need token
    if (!token) {
      setError("Interview token required");
      setInitState("error");
      setIsLoading(false);
      return;
    }

    // Prevent duplicate initialization attempts
    if (initAttemptedRef.current) {
      return;
    }

    // Use ignore flag to prevent race conditions (React best practice)
    let ignore = false;
    initAttemptedRef.current = true;

    const startResponse = async () => {
      setInitState("loading_interview");
      setIsLoading(true);
      setError(null);

      // Retry logic for handling Cosmos DB eventual consistency
      // When an interview is just published, there may be a brief delay before
      // the status change is visible to cross-partition queries
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= PROPAGATION_MAX_RETRIES; attempt++) {
        try {
          if (ignore) return;

          const res = await authFetch(`${API_URL}/respond/${token}/start`, {
            method: "POST",
          });

          // Check if this request was superseded
          if (ignore) return;

          if (!res.ok) {
            const errorData = await res.json();
            const errorMessage = errorData.error || "Failed to start interview";

            // Check if this is a "not accepting responses" error (403)
            // This could be due to eventual consistency delay after publishing
            if (
              res.status === 403 &&
              errorMessage.includes("not currently accepting responses")
            ) {
              lastError = new Error(errorMessage);

              // Only retry if we have attempts left
              if (attempt < PROPAGATION_MAX_RETRIES) {
                console.log(
                  `[Respond] Interview not active yet, retrying in ${PROPAGATION_DELAY_MS}ms (attempt ${attempt}/${PROPAGATION_MAX_RETRIES})`
                );
                await new Promise((resolve) =>
                  setTimeout(resolve, PROPAGATION_DELAY_MS)
                );
                continue; // Retry
              }
            }

            throw new Error(errorMessage);
          }

          const data = await res.json();

          // Final check before updating state
          if (ignore) return;

          setInterview(data.interview);
          setResponse(data.response);
          if (data.response.attachments?.length > 0) {
            setAttachments(
              data.response.attachments.map((attachment: Attachment) => ({
                id: attachment.id,
                name: attachment.filename,
                url: attachment.blobUrl,
                type: attachment.mimeType,
                size: attachment.size,
                uploadedAt: attachment.uploadedAt,
                analysis: attachment.analysis,
              }))
            );
          }

          // Load existing transcript
          if (data.response.transcript?.length > 0) {
            const loadedMessages = data.response.transcript.map(
              (entry: TranscriptEntry) => ({
                id: entry.id,
                role: entry.role as "user" | "assistant",
                content: entry.content,
                timestamp: new Date(entry.timestamp),
                isFinal: true,
              })
            );
            setMessages(loadedMessages);
            setLastSavedMessageCount(loadedMessages.length);

            // Figure out current question index and answered count from conversation
            // Count how many times the AI moved to a new question (look for "next question" patterns)
            const loadedQuestions = (data.interview?.questions ||
              []) as InterviewQuestion[];
            const questionPatterns = loadedMessages.filter(
              (m: VoiceMessage) =>
                m.role === "assistant" &&
                (m.content.includes("next question") ||
                  m.content.includes("Here's the next question") ||
                  m.content.includes("let's move on"))
            );
            let answeredCount = Math.min(
              questionPatterns.length,
              loadedQuestions.length
            );
            let currentIndex = Math.min(
              answeredCount,
              Math.max(0, loadedQuestions.length - 1)
            );
            const hasUserMessage = loadedMessages.some(
              (m: VoiceMessage) =>
                m.role === "user" &&
                m.content?.trim() &&
                m.content.trim() !== "..." &&
                m.content.trim() !== "…"
            );
            if (
              loadedQuestions.length > 0 &&
              currentIndex >= loadedQuestions.length - 1 &&
              hasUserMessage
            ) {
              answeredCount = loadedQuestions.length;
            }
            setCurrentQuestionIndex(currentIndex);
            setAnsweredQuestions(answeredCount);
          }

          if (data.response.status === "submitted") {
            setIsSubmitted(true);
          }

          setInitState("ready");
          setIsLoading(false);
          return; // Success - exit the retry loop
        } catch (err) {
          if (ignore) return;
          lastError =
            err instanceof Error ? err : new Error("Failed to start interview");

          // If this isn't a retryable error, break out immediately
          if (
            !lastError.message.includes("not currently accepting responses")
          ) {
            break;
          }

          // If we've exhausted retries, break out
          if (attempt >= PROPAGATION_MAX_RETRIES) {
            break;
          }
        }
      }

      // All retries exhausted or non-retryable error
      if (!ignore && lastError) {
        console.error("[Respond] Failed to start after retries:", lastError);
        setError(lastError.message);
        setInitState("error");
        setIsLoading(false);
      }
    };

    startResponse();

    // Cleanup function to prevent state updates from stale requests
    return () => {
      ignore = true;
    };
  }, [token, isReady, authFetch]);

  // Add initial greeting when interview loads - only after init is complete
  useEffect(() => {
    // Only add greeting when:
    // 1. Init is complete (ready state)
    // 2. Interview is loaded
    // 3. No messages exist yet
    // 4. Not already submitted
    if (
      initState === "ready" &&
      interview &&
      messages.length === 0 &&
      !isSubmitted
    ) {
      const greeting: VoiceMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: getInterviewerGreeting(),
        timestamp: new Date(),
        isFinal: true,
      };
      setMessages([greeting]);
    }
  }, [
    initState,
    interview,
    messages.length,
    isSubmitted,
    getInterviewerGreeting,
  ]);

  // Auto-dismiss upload errors after 6 seconds
  useEffect(() => {
    if (!uploadError) return;
    const timer = setTimeout(() => setUploadError(null), 6000);
    return () => clearTimeout(timer);
  }, [uploadError]);

  // Auto-scroll messages — scroll the container directly to avoid moving the whole page
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      requestAnimationFrame(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      });
    }
  }, [messages]);

  // Auto-resize textarea when text changes (e.g., from speech recognition)
  useEffect(() => {
    if (textareaRef.current) {
      const maxHeight = window.innerWidth < 640 ? 150 : 250;
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, maxHeight) + "px";
    }
  }, [textInput]);

  // Handle voice message from VoiceMode - also detect question progress
  const handleVoiceMessage = useCallback(
    (message: VoiceMessage) => {
      setMessages((prev) => {
        if (prev.some((m) => m.id === message.id)) return prev;
        return [...prev, message];
      });

      // Detect if AI moved to a new question
      if (message.role === "assistant") {
        const content = message.content.toLowerCase();

        // Word to number mapping
        const wordToNum: Record<string, number> = {
          one: 1,
          two: 2,
          three: 3,
          four: 4,
          five: 5,
          six: 6,
          seven: 7,
          eight: 8,
          nine: 9,
          ten: 10,
          first: 1,
          second: 2,
          third: 3,
          fourth: 4,
          fifth: 5,
          sixth: 6,
          seventh: 7,
          eighth: 8,
          ninth: 9,
          tenth: 10,
        };

        // Check for patterns like "question 5", "question five", "question number 5"
        // The voice AI is instructed to use: "moving to question [NUMBER]:" format
        const numberedPatterns = [
          /moving\s+to\s+question\s+(\d+)/i, // Primary format from AI instructions
          /question\s+(\d+):/i, // "question 2:" format
          /question\s+(\d+)/i,
          /question\s+(one|two|three|four|five|six|seven|eight|nine|ten)/i,
          /question\s+number\s+(\d+)/i,
          /moving\s+(?:on\s+)?to\s+question\s+(one|two|three|four|five|six|seven|eight|nine|ten)/i,
        ];

        let progressUpdated = false;

        for (const pattern of numberedPatterns) {
          const match = content.match(pattern);
          if (match) {
            let questionNum: number;
            const captured = match[1];

            if (/^\d+$/.test(captured)) {
              questionNum = parseInt(captured, 10);
            } else {
              questionNum = wordToNum[captured.toLowerCase()] || 0;
            }

            if (questionNum > 0 && questionNum <= questions.length) {
              const newIndex = questionNum - 1; // Convert to 0-based index
              if (newIndex > currentQuestionIndex) {
                setCurrentQuestionIndex(newIndex);
                setAnsweredQuestions(newIndex);
                progressUpdated = true;
              }
            }
            break;
          }
        }

        // Check for "next question" phrases without explicit number
        // This handles cases like "let's move on to the next question"
        if (!progressUpdated) {
          const nextQuestionPhrases = [
            /move\s+on\s+to\s+the\s+next\s+question/i,
            /let's\s+move\s+(?:on\s+)?to\s+the\s+next\s+question/i,
            /(?:now|here's|here is)\s+the\s+next\s+question/i,
            /proceed\s+to\s+the\s+next\s+question/i,
            /on\s+to\s+(?:the\s+)?next\s+question/i,
            /next\s+question[.:]/i,
          ];

          if (nextQuestionPhrases.some((pattern) => pattern.test(content))) {
            // Increment to next question if not at the end
            if (currentQuestionIndex < questions.length - 1) {
              const newIndex = currentQuestionIndex + 1;
              setCurrentQuestionIndex(newIndex);
              setAnsweredQuestions(newIndex);
              progressUpdated = true;
            }
          }
        }

        // Also check if message contains exact question text
        questions.forEach((q, index) => {
          if (
            message.content.includes(q.text) &&
            index > currentQuestionIndex
          ) {
            setCurrentQuestionIndex(index);
            setAnsweredQuestions(index);
          }
        });

        // Check for completion indicators
        const completionPhrases = [
          "covered all the questions",
          "that's all the questions",
          "completed all",
          "answered all",
          "concludes all",
          "that concludes",
          "all of our questions",
          "final question",
          "last question",
          "no more questions",
          "end of the interview",
          "end of our interview",
          "thank you for completing",
          "finished all the questions",
        ];

        if (completionPhrases.some((phrase) => content.includes(phrase))) {
          setAnsweredQuestions(questions.length);
          setCurrentQuestionIndex(questions.length - 1);
        }
      }
    },
    [questions, currentQuestionIndex]
  );

  // Save transcript to API
  const saveTranscript = useCallback(
    async (messagesToSave: VoiceMessage[]) => {
      if (!response || messagesToSave.length === 0) return;

      try {
        const transcriptEntries = messagesToSave.map((msg) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp:
            msg.timestamp instanceof Date
              ? msg.timestamp.toISOString()
              : String(msg.timestamp),
        }));

        await authFetch(`${API_URL}/responses/${response.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ appendTranscript: transcriptEntries }),
        });
      } catch (err) {
        console.error("[Respond] Failed to save transcript:", err);
      }
    },
    [response, authFetch]
  );

  // Auto-save transcript periodically
  useEffect(() => {
    const unsavedMessages = messages.slice(lastSavedMessageCount);
    if (unsavedMessages.length >= 2) {
      saveTranscript(unsavedMessages).then(() => {
        setLastSavedMessageCount(messages.length);
      });
    }
  }, [messages, lastSavedMessageCount, saveTranscript]);

  // Handle voice mode close - sync messages
  const handleVoiceModeClose = useCallback(async () => {
    const unsavedMessages = messages.slice(lastSavedMessageCount);
    if (unsavedMessages.length > 0) {
      await saveTranscript(unsavedMessages);
      setLastSavedMessageCount(messages.length);
    }
    setIsVoiceOpen(false);
  }, [messages, lastSavedMessageCount, saveTranscript]);

  // Continuous speech-to-text refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const committedTextRef = useRef("");

  // Toggle speech-to-text (continuous mode)
  const toggleSpeechToText = async () => {
    // If already recording, stop it
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    // Check for Web Speech API support
    const SpeechRecognitionConstructor =
      (
        window as Window & {
          webkitSpeechRecognition?: SpeechRecognitionConstructor;
          SpeechRecognition?: SpeechRecognitionConstructor;
        }
      ).webkitSpeechRecognition ||
      (
        window as Window & { SpeechRecognition?: SpeechRecognitionConstructor }
      ).SpeechRecognition;

    if (!SpeechRecognitionConstructor) {
      alert(
        "Speech recognition is not supported in this browser. Please use Chrome or Edge."
      );
      return;
    }

    // Store current text as the base to append to
    const existingText = textInput;
    const needsSpace = existingText.length > 0 && !existingText.endsWith(" ");
    committedTextRef.current = existingText + (needsSpace ? " " : "");

    const recognition = new SpeechRecognitionConstructor();
    recognitionRef.current = recognition;

    recognition.continuous = true; // Keep listening continuously
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      setIsRecording(true);
    };

    recognition.onresult = (event: SpeechRecognitionEventLike) => {
      let interimTranscript = "";
      let finalTranscript = "";

      // Process results - separate final from interim
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }

      // When we get final results, commit them to our base text
      if (finalTranscript) {
        committedTextRef.current += finalTranscript;
      }

      // Update the text input with committed text + current interim
      setTextInput(committedTextRef.current + interimTranscript);
    };

    recognition.onend = () => {
      setIsRecording(false);
      recognitionRef.current = null;
    };

    recognition.onerror = (event: SpeechRecognitionErrorEventLike) => {
      console.error("Speech recognition error:", event.error);
      // Don't stop for no-speech errors in continuous mode
      if (event.error !== "no-speech") {
        setIsRecording(false);
        recognitionRef.current = null;
      }
    };

    recognition.start();
  };

  // Cleanup recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // File upload handler with multimodal analysis
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

    // Validate file sizes before starting upload
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(
          `File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 50MB.`
        );
        return;
      }
      if (file.size === 0) {
        setUploadError(`File "${file.name}" is empty.`);
        return;
      }
    }

    setIsUploading(true);
    let persistedAttachments = response?.attachments
      ? [...response.attachments]
      : [];

    try {
      for (const file of Array.from(files)) {
        const mimeType = file.type || "application/octet-stream";
        // Create local preview URL for images
        const isImage = mimeType.startsWith("image/");
        const previewUrl = isImage ? URL.createObjectURL(file) : undefined;

        // Get SAS URL for upload
        const sasRes = await authFetch(`${API_URL}/uploads/sas`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            filename: file.name,
            mimeType,
            size: file.size,
          }),
        });

        if (!sasRes.ok) {
          throw new Error("Failed to get upload URL");
        }

        const { uploadUrl, blobUrl } = await sasRes.json();

        // Upload file directly to blob storage
        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": mimeType,
          },
          body: file,
        });
        if (!uploadResponse.ok) {
          throw new Error("Failed to upload file");
        }

        // Determine file type description
        const getFileTypeDescription = (mimeType: string, fileName: string) => {
          if (mimeType.startsWith("image/")) return "image";
          if (mimeType.includes("pdf")) return "PDF document";
          if (
            mimeType.includes("presentation") ||
            fileName.endsWith(".pptx") ||
            fileName.endsWith(".ppt")
          )
            return "presentation";
          if (
            mimeType.includes("word") ||
            fileName.endsWith(".docx") ||
            fileName.endsWith(".doc")
          )
            return "Word document";
          if (
            mimeType.includes("excel") ||
            fileName.endsWith(".xlsx") ||
            fileName.endsWith(".xls")
          )
            return "Excel spreadsheet";
          if (mimeType.startsWith("audio/")) return "audio file";
          if (mimeType.startsWith("video/")) return "video file";
          if (
            mimeType.includes("zip") ||
            mimeType.includes("compressed") ||
            fileName.endsWith(".zip") ||
            fileName.endsWith(".rar") ||
            fileName.endsWith(".7z")
          )
            return "compressed archive";
          if (
            mimeType.includes("json") ||
            mimeType.includes("xml") ||
            fileName.endsWith(".json") ||
            fileName.endsWith(".xml")
          )
            return "data file";
          if (mimeType.includes("text")) return "text file";
          return "document";
        };

        const fileTypeDesc = getFileTypeDescription(file.type, file.name);
        const attachmentId = crypto.randomUUID();

        // Add user message with file info
        const fileMessage: VoiceMessage = {
          id: crypto.randomUUID(),
          role: "user",
          content: `📎 Shared ${isImage ? "an" : "a"} ${fileTypeDesc}: ${
            file.name
          }`,
          timestamp: new Date(),
          isFinal: true,
        };
        setMessages((prev) => [...prev, fileMessage]);

        // Analyze all files (images get vision analysis, documents get text extraction)
        let analysisText = "";
        try {
          const analyzeRes = await authFetch(`${API_URL}/uploads/analyze`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              blobUrl,
              mimeType,
              filename: file.name,
              context: currentQuestion?.text || interview?.title,
            }),
          });

          if (analyzeRes.ok) {
            const { analysis } = await analyzeRes.json();
            analysisText =
              analysis?.relevantContent || analysis?.description || "";
          }
        } catch (err) {
          console.warn("File analysis failed, continuing without:", err);
        }

        // Always add AI response acknowledging the file
        const aiResponseContent =
          isImage && analysisText
            ? `I can see the image you shared. ${analysisText}`
            : `Thanks for sharing "${file.name}". ${
                analysisText ||
                `I've noted this ${fileTypeDesc} for our conversation. Feel free to reference it or ask me any questions about it.`
              }`;

        const analysisMessage: VoiceMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: aiResponseContent,
          timestamp: new Date(),
          isFinal: true,
        };
        setMessages((prev) => [...prev, analysisMessage]);

        const attachmentRecord = {
          id: attachmentId,
          filename: file.name,
          mimeType,
          size: file.size,
          blobUrl,
          uploadedAt: new Date().toISOString(),
          analysis: analysisText || undefined,
        };

        // Persist attachment on the server before updating local UI state
        if (response) {
          persistedAttachments = [...persistedAttachments, attachmentRecord];
          const saveRes = await authFetch(
            `${API_URL}/responses/${response.id}`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ attachments: persistedAttachments }),
            }
          );
          if (!saveRes.ok) {
            throw new Error("Failed to save attachment");
          }
          setResponse((prev) =>
            prev ? { ...prev, attachments: persistedAttachments } : prev
          );
        }

        // Only add to the visible attachment list after server confirmation
        setAttachments((prev) => [
          ...prev,
          {
            id: attachmentId,
            name: file.name,
            url: blobUrl,
            type: mimeType,
            size: file.size,
            uploadedAt: attachmentRecord.uploadedAt,
            previewUrl,
            analysis: analysisText,
          },
        ]);
      }
    } catch (err) {
      console.error("File upload failed:", err);
      setUploadError("Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const getBlobNameFromUrl = useCallback((url: string) => {
    const marker = ".blob.core.windows.net/";
    const markerIndex = url.indexOf(marker);
    if (markerIndex === -1) return null;
    const path = url.slice(markerIndex + marker.length);
    const [, ...blobParts] = path.split("/");
    if (blobParts.length === 0) return null;
    return blobParts.join("/");
  }, []);

  const removeAttachment = useCallback(
    async (index: number) => {
      const attachment = attachments[index];
      setAttachments((prev) => prev.filter((_, i) => i !== index));

      if (!response || !attachment?.id) return;

      const updatedAttachments = (response.attachments || []).filter(
        (item) => item.id !== attachment.id
      );

      try {
        const saveRes = await authFetch(
          `${API_URL}/responses/${response.id}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attachments: updatedAttachments }),
          }
        );
        if (!saveRes.ok) {
          console.warn("[Respond] Failed to remove attachment from response");
          return;
        }
        setResponse((prev) =>
          prev ? { ...prev, attachments: updatedAttachments } : prev
        );

        const blobName = attachment?.url
          ? getBlobNameFromUrl(attachment.url)
          : null;
        if (blobName) {
          await authFetch(`${API_URL}/uploads/${encodeURIComponent(blobName)}`, {
            method: "DELETE",
          });
        }
      } catch (err) {
        console.warn("[Respond] Failed to remove attachment:", err);
      }
    },
    [attachments, authFetch, getBlobNameFromUrl, response]
  );

  const sendUserMessage = useCallback(
    (messageText: string) => {
      const trimmedMessage = messageText.trim();
      if (!trimmedMessage || isSending) return;

      if (
        questions.length > 0 &&
        currentQuestionIndex >= questions.length - 1 &&
        answeredQuestions < questions.length
      ) {
        setAnsweredQuestions(questions.length);
      }

      setIsSending(true);
      const userMessage: VoiceMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: trimmedMessage,
        timestamp: new Date(),
        isFinal: true,
      };

      setMessages((prev) => [...prev, userMessage]);
      setTextInput("");

      // Generate AI response
      setTimeout(() => {
        const aiResponse = generateAIResponse(trimmedMessage);
        const aiMessage: VoiceMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: aiResponse,
          timestamp: new Date(),
          isFinal: true,
        };
        setMessages((prev) => [...prev, aiMessage]);
        setIsSending(false);
      }, 800);
    },
    [
      answeredQuestions,
      currentQuestionIndex,
      generateAIResponse,
      isSending,
      questions.length,
    ]
  );

  // Send text message with AI response
  const sendTextMessage = () => {
    // If mic is recording, stop it so it turns off when user sends
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
      setIsRecording(false); // Update UI immediately; onend will also fire
    }
    sendUserMessage(textInput);
  };

  const handleOptionSelect = useCallback(
    (option: string) => {
      if (!option.trim()) return;
      sendUserMessage(option);
    },
    [sendUserMessage]
  );

  // Submit response
  const handleSubmit = async () => {
    if (!response || isSubmitting) return;

    // Save any remaining messages first
    const unsavedMessages = messages.slice(lastSavedMessageCount);
    if (unsavedMessages.length > 0) {
      await saveTranscript(unsavedMessages);
    }

    setIsSubmitting(true);
    try {
      const res = await authFetch(
        `${API_URL}/responses/${response.id}/submit`,
        {
          method: "POST",
        }
      );

      if (res.ok) {
        setIsSubmitted(true);
      } else {
        const errorData = await res.json();
        setError(errorData.error || "Failed to submit");
      }
    } catch {
      setError("Failed to submit response");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render markdown-like bold text (supports bold spanning newlines)
  const renderMessageContent = (content: string) => {
    const parts = content.split(/\*\*([\s\S]*?)\*\*/g);
    return parts.map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );
  };

  // Loading state - show different messages based on init state
  if (
    isLoading ||
    initState === "waiting_auth" ||
    initState === "loading_interview"
  ) {
    const loadingMessage =
      initState === "waiting_auth"
        ? "Authenticating..."
        : "Loading interview...";

    return (
      <div className="flex-1 flex items-center justify-center py-12" role="status">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
          <p className="text-muted">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Unable to Load Interview
          </h3>
          <p className="text-muted mb-4">{error}</p>
          <button onClick={() => navigate("/")} className="btn btn-primary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Submitted state
  if (isSubmitted) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4 animate-fade-in">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            Thank You!
          </h2>
          <p className="text-muted">
            Your response has been submitted successfully.
          </p>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Your Conversation
          </h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                    message.role === "user"
                      ? "bg-primary text-white"
                      : "bg-user-msg text-foreground"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">
                    {renderMessageContent(message.content)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-6">
          <button onClick={() => navigate("/")} className="btn btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-8rem)] px-4">
      {/* Header */}
      <div className="py-4 border-b border-border">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-foreground">
              {interview?.title}
            </h1>
            {interview?.description && (
              <p className="text-sm text-muted mt-1">{interview.description}</p>
            )}
          </div>

          {/* Submit button - only show when ready */}
          {(questions.length > 0
            ? answeredQuestions >= questions.length
            : messages.length >= 6) && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="shrink-0 px-4 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
              aria-label={isSubmitting ? "Submitting response" : "Submit response"}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="hidden sm:inline">Submitting...</span>
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="hidden sm:inline">Submit</span>
                </>
              )}
            </button>
          )}
        </div>

        {questions.length > 0 && (
          <div className="flex items-center gap-2 mt-3">
            <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={questions.length} aria-valuenow={answeredQuestions} aria-label="Interview progress">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{
                  // Progress shows current question position
                  // If completed, show 100%. Otherwise show position based on current index
                  width: `${
                    answeredQuestions >= questions.length
                      ? 100
                      : (currentQuestionIndex / questions.length) * 100
                  }%`,
                }}
              />
            </div>
            <span className="text-xs text-muted">
              {answeredQuestions >= questions.length
                ? `${questions.length}/${questions.length} complete`
                : `Q${currentQuestionIndex + 1} of ${questions.length}`}
            </span>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto py-4">
        <div className="space-y-4" role="log" aria-live="polite" aria-relevant="additions">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {message.role === "assistant" && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center mr-2 shrink-0">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>
              )}
              <div
                className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                  message.role === "user"
                    ? "bg-primary text-white"
                    : "bg-card border border-border text-foreground"
                }`}
              >
                <p className="text-sm whitespace-pre-wrap leading-relaxed">
                  {renderMessageContent(message.content)}
                </p>
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isSending && (
            <div className="flex justify-start" role="status">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center mr-2">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <div className="bg-card border border-border px-4 py-3 rounded-2xl">
                <div className="flex gap-1">
                  <span className="sr-only">Assistant is typing</span>
                  <div
                    className="w-2 h-2 bg-muted rounded-full animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  />
                  <div
                    className="w-2 h-2 bg-muted rounded-full animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  />
                  <div
                    className="w-2 h-2 bg-muted rounded-full animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Upload Error Banner */}
      {uploadError && (
        <div role="alert" className="mx-2 mb-2 px-3 py-2 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 flex items-center gap-2">
          <span className="text-sm text-red-700 dark:text-red-300 flex-1">
            {uploadError}
          </span>
          <button
            onClick={() => setUploadError(null)}
            className="shrink-0 text-red-500 hover:text-red-700 dark:hover:text-red-300"
            aria-label="Dismiss"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Structured Question Options */}
      {showChoiceOptions && (
        <div className="px-2 pb-2" role="group" aria-label="Answer options">
          <p className="text-xs text-muted mb-2">
            {currentQuestion?.type === "rating"
              ? "Select a rating:"
              : currentQuestion?.type === "yes_no"
                ? "Select your answer:"
                : "Choose an option (or type your own):"}
          </p>
          <div className="flex flex-wrap gap-2">
            {currentQuestionOptions.map((option, index) => (
              <button
                key={`${option}-${index}`}
                type="button"
                onClick={() => handleOptionSelect(option)}
                disabled={isSending}
                className="px-3 py-1.5 rounded-full border border-border bg-card text-foreground text-sm hover:bg-user-msg transition-colors disabled:opacity-50"
              >
                {currentQuestion?.type === "multiple_choice" && (
                  <span className="font-semibold mr-1.5">{getOptionLabel(index)}.</span>
                )}
                {option}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="flex gap-2 px-2 py-2 overflow-x-auto">
          {attachments.map((file, index) => (
            <div key={index} className="shrink-0 relative group">
              {file.previewUrl ? (
                // Image thumbnail
                <div className="w-16 h-16 rounded-lg overflow-hidden border border-border bg-card">
                  <img
                    src={file.previewUrl}
                    alt={file.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                // File icon
                <div className="w-16 h-16 rounded-lg border border-border bg-card flex flex-col items-center justify-center gap-1">
                  <svg
                    className="w-6 h-6 text-muted"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span className="text-[10px] text-muted truncate max-w-14 px-1">
                    {file.name.split(".").pop()?.toUpperCase()}
                  </span>
                </div>
              )}
              {/* Remove button */}
              <button
                onClick={() => removeAttachment(index)}
                className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                aria-label="Remove attachment"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input Area */}
      <div className="py-3 sm:py-4 border-t border-border safe-area-bottom">
        <div className="flex items-end gap-1.5 sm:gap-2">
          {/* File Upload Button */}
          {interview?.settings?.allowFileUpload && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileUpload}
                aria-label="Upload file"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl bg-user-msg text-muted hover:text-foreground hover:bg-border transition-colors disabled:opacity-50"
                aria-label="Attach file"
                title="Attach file"
              >
                {isUploading ? (
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                )}
              </button>
            </>
          )}

          {/* Text Input */}
          <div className="flex-1 min-w-0 relative">
            <div className="rounded-2xl border border-border bg-card overflow-hidden focus-within:ring-2 focus-within:ring-primary/50 transition-shadow">
              <textarea
                ref={textareaRef}
                value={textInput}
                aria-label="Your response"
                onChange={(e) => {
                  setTextInput(e.target.value);
                  // Auto-resize: use smaller max on mobile (150px) vs desktop (250px)
                  const maxHeight = window.innerWidth < 640 ? 150 : 250;
                  e.target.style.height = "auto";
                  e.target.style.height =
                    Math.min(e.target.scrollHeight, maxHeight) + "px";
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendTextMessage();
                  }
                }}
                placeholder={inputPlaceholder}
                rows={1}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-transparent text-foreground text-sm sm:text-base focus:outline-none resize-none transition-[height] duration-150 overflow-y-auto"
                disabled={isSending}
                style={{ minHeight: "44px" }}
              />
            </div>
          </div>

          {/* Speech-to-Text Button (Toggle) */}
          <button
            onClick={toggleSpeechToText}
            disabled={isSending}
            className={`shrink-0 w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl transition-colors ${
              isRecording
                ? "bg-red-500 text-white animate-pulse"
                : "bg-user-msg text-muted hover:text-foreground hover:bg-border"
            }`}
            aria-label={isRecording ? "Stop recording" : "Start dictation"}
            title={isRecording ? "Click to stop" : "Click to dictate"}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
          </button>

          {/* Voice Mode Button - Headphones icon */}
          {interview?.settings?.allowVoice && (
            <button
              onClick={() => setIsVoiceOpen(true)}
              className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white hover:opacity-90 transition-opacity"
              aria-label="Voice conversation"
              title="Voice conversation"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.536 8.464a5 5 0 010 7.072M12 12h.01M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728"
                />
              </svg>
            </button>
          )}

          {/* Send Button */}
          <button
            onClick={sendTextMessage}
            disabled={!textInput.trim() || isSending}
            className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl bg-primary text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-hover transition-colors"
            aria-label="Send message"
            title="Send message"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 10l7-7m0 0l7 7m-7-7v18"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Voice Mode Modal */}
      <VoiceMode
        isOpen={isVoiceOpen}
        onClose={handleVoiceModeClose}
        sessionId={response?.id}
        onMessage={handleVoiceMessage}
        previousMessages={messages}
        currentQuestion={currentQuestion?.text}
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={questions.length}
        answeredQuestions={answeredQuestions}
        allQuestions={questions.map((q) => ({
          text: q.text,
          id: q.id,
          type: q.type,
          options: q.options,
        }))}
        attachments={attachments.map((a) => ({
          id: a.id,
          name: a.name,
          type: a.type,
          analysis: a.analysis,
          uploadedAt: a.uploadedAt,
        }))}
        onSubmit={handleSubmit}
        onFileUpload={handleFileUpload}
        isUploading={isUploading}
        allowFileUpload={interview?.settings?.allowFileUpload}
      />
    </div>
  );
}
